/**
 * Utility functions for handling selection logic and preventing unnecessary API calls
 */

export function isSameSelection(
  currentValue: string | null | undefined,
  newValue: string | null | undefined,
  caseSensitive: boolean = true,
): boolean {
  if (currentValue === newValue) return true

  if (!caseSensitive && currentValue && newValue) {
    return currentValue.toLowerCase() === newValue.toLowerCase()
  }

  return false
}

/**
 * Validates that all provided parameters are present and not empty
 * @param params - Object containing the parameters to validate (all optional)
 * @returns true if all provided parameters are valid, false if any provided parameter is invalid
 */
export function validateRequiredParams(params: {
  brandID?: string | null
  country?: string | null
  engine?: string | null
}): boolean {
  const { brandID, country, engine } = params

  // Only validate parameters that are provided (not undefined)
  const validations: boolean[] = []

  if (brandID !== undefined) {
    validations.push(!!(brandID && brandID.trim() !== ''))
  }

  if (country !== undefined) {
    validations.push(!!(country && country.trim() !== ''))
  }

  if (engine !== undefined) {
    validations.push(!!(engine && engine.trim() !== ''))
  }

  // Return true if no validations were needed (no parameters provided) or all validations passed
  return validations.length === 0 || validations.every(Boolean)
}

/**
 * Query string shared by the brand presence and advertiser presence endpoints. The optional
 * lists are appended as repeated keys, the form the API's array DTOs accept.
 */
export function buildPresenceQuery(params: {
  country: string
  startDate: Date
  endDate: Date
  brandID: string
  engine: string
  keywordIDs?: string[]
  advertiserIDs?: string[]
}): URLSearchParams {
  const { country, startDate, endDate, brandID, engine, keywordIDs, advertiserIDs } = params
  const query = new URLSearchParams({
    country,
    startDate: startDate.toISOString(),
    endDate: endDate.toISOString(),
    brandID,
    engine,
  })
  keywordIDs?.forEach((id) => query.append('keywordIDs', id))
  advertiserIDs?.forEach((id) => query.append('advertiserIDs', id))
  return query
}
